import { useEffect, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import { FiAlertTriangle } from 'react-icons/fi';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { isReloadOrCloseKey } from '@/components/ExitGuard';

/**
 * While `active` (e.g. an export is running): in-app navigation and reload / close
 * shortcuts open a "please wait" dialog with no way to leave. The browser's reload
 * button / tab close / browser close can't show custom UI, so those get the
 * browser's own "Leave site?" prompt.
 * Mount once per page; react-router allows one blocker at a time.
 */
export default function BusyGuard({ active, title, message }: { active: boolean; title: string; message: string }) {
  const blocker = useBlocker(active);
  const [keyBlocked, setKeyBlocked] = useState(false);

  useEffect(() => {
    if (!active) {
      setKeyBlocked(false);
      return undefined;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isReloadOrCloseKey(e)) return;
      e.preventDefault();
      e.stopPropagation();
      setKeyBlocked(true);
    };
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('keydown', onKeyDown, true);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('keydown', onKeyDown, true);
    };
  }, [active]);

  // Work finished while the dialog was up: drop the blocked navigation, user can click again.
  useEffect(() => {
    if (!active && blocker.state === 'blocked') blocker.reset();
  }, [active, blocker]);

  const stay = () => {
    setKeyBlocked(false);
    if (blocker.state === 'blocked') blocker.reset();
  };

  return (
    <Dialog open={active && (keyBlocked || blocker.state === 'blocked')} onOpenChange={(open) => !open && stay()}>
      <DialogContent className="z-[1100] max-w-md" overlayClassName="z-[1090]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-destructive/10 text-destructive">
              <FiAlertTriangle size={16} />
            </span>
            {title}
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">{message}</p>
        <div className="flex justify-end">
          <Button onClick={stay}>OK, I'll wait</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
